import { ProjectType, ResearchType } from '@empathy/global/data';

import { Project } from './project.model';
import { ResearchMethod } from './research-method.model';
import { ResearchStatus } from './research-status.enum';

export class GridProject {
  id: string;
  projectName: string;
  name: string;
  description: string;
  incidence: string;
  maxResponses: string;
  mixed: boolean;
  progress: string;
  researchStatus: number;
  projectTypeId: ProjectType;
  projectMethodTypeId: ResearchType[];
  researchMethodId: string;
  researchMethodIdv2: string;
  response: string;
  startDate: Date;
  isStarted: boolean;
  isLiveEditing: boolean;
  hasSurveyJson: boolean;
  numResponses = 0;

  constructor(project: Project) {
    this.mapFromProject(project);
  }

  private mapFromProject(project: Project): void {
    this.id = project.id;
    this.maxResponses = '0';
    this.projectName = project.name;
    this.mixed = this.isMixed(project.methods);
    this.projectTypeId = project.projectTypeId;
    this.projectMethodTypeId = project.profileSurvey ? [ResearchType.Survey] : this.extractResearchTypes(project.methods);
    this.researchMethodId = this.getResearchMethodId(project.methods);
    this.name = this.getResearchMethodName(project.methods, this.researchMethodId);
    this.researchStatus = this.getResearchMethodStatus(project.methods, this.researchMethodId);
    this.numResponses = this.getResearchMethodNumResponses(project.methods, this.researchMethodId);
    this.researchMethodIdv2 = this.getResearchMethodIdv2(project.methods);
    this.response = '0';
    this.startDate = project.createdDateTime;
    this.isLiveEditing = project.methods?.some((method) => method.status === ResearchStatus.LiveEditing);

    if (project.methods[0].researchType === ResearchType.Survey) {
      this.isStarted = !!project.methods[0].projectSurveyContent?.notepadJson || !!project.methods[0].projectSurveyContent?.surveyJson;
      this.hasSurveyJson = !!project.methods[0].projectSurveyContent?.surveyJson;
    }
  }

  private extractResearchTypes(methods: ResearchMethod[]): number[] {
    const uniqueTypes = new Set<number>();
    methods.forEach((item) => uniqueTypes.add(item.researchType));
    return Array.from(uniqueTypes);
  }

  private isMixed(methods: ResearchMethod[]): boolean {
    return methods?.filter((method) => method.status !== ResearchStatus.LiveEditing).length > 1;
  }

  private getResearchMethodId(methods: ResearchMethod[]): string {
    return methods?.filter((method) => method.status !== ResearchStatus.LiveEditing).length === 1 ? methods[0].id : null;
  }

  private getResearchMethodNumResponses(methods: ResearchMethod[], researchMethodId: string): number {
    return methods?.find((method) => method.id === researchMethodId)?.numResponses ?? 0;
  }

  private getResearchMethodStatus(methods: ResearchMethod[], researchMethodId: string): ResearchStatus {
    return methods?.find((method) => method.id === researchMethodId)?.status ?? 0;
  }

  private getResearchMethodName(methods: ResearchMethod[], researchMethodId: string): string {
    return this.mixed ? this.projectName : methods?.find((method) => method.id === researchMethodId)?.researchDetails.title;
  }

  private getResearchMethodIdv2(methods: ResearchMethod[]): string {
    return methods?.find((m) => m.status === ResearchStatus.LiveEditing)?.id;
  }
}

export class GridProjects {
  projects: GridProject[] = [];

  constructor(
    public totalCount: number,
    projects: Project[]
  ) {
    this.mapFromProjects(projects);
  }

  private mapFromProjects(projects: Project[]): void {
    this.projects = projects.map((project) => new GridProject(project));
  }
}
